import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { streamPost } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { MicButton } from "@/components/MicButton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const CONFIG = {
  landing: {
    endpoint: "/guide/landing",
    title: "Ask Lex",
    subtitle: "Your guide to Legalis AI",
    greeting: "Hi, I'm Lex. I can explain what Legalis AI does, our agents, pricing, and how to get started. What would you like to know?",
    prompts: ["What can Legalis AI do?", "How much does it cost?", "How do I get free access?"],
  },
  app: {
    endpoint: "/guide/app",
    title: "Lex · your copilot",
    subtitle: "Guides you & speeds up tasks",
    greeting: "Hi, I'm Lex — your in-app guide. Ask me how to do anything: build a market-entry roadmap, compare two countries, save research, redeem a code, and more.",
    prompts: ["How do I build a market-entry roadmap?", "Compare two jurisdictions", "How do I redeem a discount code?"],
  },
};

export function ChatWidget({ mode = "landing" }) {
  const cfg = CONFIG[mode] || CONFIG.landing;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: cfg.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    setBusy(true);
    await streamPost(cfg.endpoint, { message: msg, history }, {
      onToken: (t) => setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t };
        return c;
      }),
      onDone: () => { setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; }); setBusy(false); },
      onError: (err) => { setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: err || "Something went wrong. Please try again." }; return c; }); setBusy(false); },
      onClose: () => { setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; }); setBusy(false); },
    });
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-launcher"
          className="btn-plum fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full p-3 sm:pl-4 sm:pr-5 hover:-translate-y-0.5 transition-transform"
          aria-label="Open Lex assistant"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline text-sm font-medium">Ask Lex</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[80vh] bg-card border border-border flex flex-col shadow-2xl"
        >
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border bg-brand/10">
            <div className="w-8 h-8 grid place-items-center bg-brand text-white shrink-0"><Sparkles className="w-4 h-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-serif text-sm font-semibold leading-tight">{cfg.title}</div>
              <div className="text-[11px] text-muted-foreground truncate">{cfg.subtitle}</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="chat-widget-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-widget-messages">
            {messages.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="bg-primary text-primary-foreground px-3 py-2 text-sm max-w-[85%]">{m.content}</div>
                </div>
              ) : (
                <div key={i} className="flex gap-2">
                  <div className="w-6 h-6 grid place-items-center bg-brand/15 border border-brand text-brand shrink-0 mt-0.5"><Sparkles className="w-3 h-3" /></div>
                  <div className="text-sm max-w-[85%] min-w-0 [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0.5">
                    {m.content ? <Markdown content={m.content} /> : <Loader2 className="w-4 h-4 animate-spin text-brand" />}
                    {m.streaming && m.content && <span className="inline-block w-1.5 h-3.5 bg-brand/70 animate-pulse align-middle ml-0.5" />}
                  </div>
                </div>
              )
            ))}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {cfg.prompts.map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid="chat-widget-prompt"
                    className="text-xs border border-border px-2.5 py-1.5 text-muted-foreground hover:border-brand hover:text-foreground transition-colors text-left">
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-border p-2.5 flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask anything…"
              rows={1}
              className="rounded-none resize-none min-h-[40px] max-h-28 text-sm"
              data-testid="chat-widget-input"
            />
            {localStorage.getItem("token") && (
              <MicButton onTranscript={(t) => setInput((v) => (v ? v + " " + t : t))} disabled={busy} className="h-10 w-10" testid="chat-widget-mic" />
            )}
            <Button onClick={() => send()} disabled={busy || !input.trim()} className="rounded-none h-10 px-3 shrink-0" data-testid="chat-widget-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
